import { useEffect, useState } from 'react';
import { SEOHead } from '../../../components/seo/SEOHead';
import { DataTable } from '../../../components/admin/DataTable';
import { Select } from '../../../components/ui/Input';
import { Spinner } from '../../../components/ui/Card';
import { api } from '../../../lib/api';

interface LeadRow {
  id: string;
  name: string;
  email: string | null;
  phone: string;
  service_interest: string | null;
  status: 'new' | 'contacted' | 'converted' | 'closed';
  created_at: string;
}

export default function LeadList() {
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  function loadLeads() {
    api.admin.leads
      .list()
      .then((res) => setLeads(res.items as LeadRow[]))
      .catch(() => setLeads([]))
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    loadLeads();
  }, []);

  async function handleStatusChange(leadId: string, status: string) {
    try {
      await api.admin.leads.updateStatus(leadId, status);
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: status as LeadRow['status'] } : l)));
    } catch {
      alert('Could not update lead status. Please try again.');
    }
  }

  return (
    <>
      <SEOHead title="Leads — Admin" description="" canonicalPath="/admin/leads" />

      <h1 className="font-display text-2xl font-bold text-ink">Contact Leads</h1>

      <div className="mt-4">
        {isLoading ? (
          <Spinner className="h-6 w-6 text-seal-blue" />
        ) : (
          <DataTable
            columns={[
              { header: 'Name', render: (l) => l.name },
              { header: 'Phone', render: (l) => l.phone },
              { header: 'Email', render: (l) => l.email ?? '—' },
              { header: 'Date', render: (l) => new Date(l.created_at).toLocaleDateString('en-IN') },
              {
                header: 'Status',
                render: (l) => (
                  <Select
                    value={l.status}
                    onChange={(e) => handleStatusChange(l.id, e.target.value)}
                    className="w-40 py-1.5 text-sm"
                  >
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                    <option value="closed">Closed</option>
                  </Select>
                ),
              },
            ]}
            rows={leads}
            keyExtractor={(l) => l.id}
            emptyMessage="No leads yet."
          />
        )}
      </div>
    </>
  );
}
